import type { CostTierId, CurrentPricingRouteView, ModelView } from "./contracts.ts";
import { sha256Hex } from "./sha256.ts";

export interface TaskFormula {
  id: string;
  tokenCount: number;
  cacheShare: number;
  inputShare: number;
  outputShare: number;
  tierBoundaries?: readonly [number, number, number, number, number];
}

export interface WholeRoutePricingResult {
  cacheRead: number | null;
  input: number | null;
  output: number | null;
  value: number | null;
  costTier: CostTierId;
  source: string;
  fallback: boolean;
  cacheFallback: boolean;
  isTaskPrice: boolean;
  routeKind: "creator-msrp" | "openrouter" | "legacy-flat" | "unavailable";
  provider: string | null;
  tag: string | null;
  fingerprint: string | null;
  observationTime: string | null;
  endpointUrl: string | null;
  endpointSha256: string | null;
  freshness: "current" | "stale" | "archive" | "legacy" | "unavailable";
  diagnostics: string[];
}

function finite(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

function stable(value: number): number {
  return Math.round(value * 1_000_000_000) / 1_000_000_000;
}

export function wholeRouteTaskCost(route: {
  cacheReadPricePerMillion: number | null;
  inputPricePerMillion: number | null;
  outputPricePerMillion: number | null;
}, activity: TaskFormula): number | null {
  const input = finite(route.inputPricePerMillion);
  const output = finite(route.outputPricePerMillion);
  const cache = finite(route.cacheReadPricePerMillion);
  if (input === null || output === null || (route.cacheReadPricePerMillion !== null && cache === null)) return null;
  const perMillion = (cache ?? input) * activity.cacheShare + input * activity.inputShare + output * activity.outputShare;
  return stable(perMillion * activity.tokenCount / 1_000_000);
}

const TASK_TIERS: readonly CostTierId[] = ["cheap", "utility", "value", "core", "premium"];
function taskTier(value: number | null, activity: TaskFormula): CostTierId {
  if (value === null) return "unknown";
  const boundaries = activity.tierBoundaries ?? [0.075, 0.25, 1, 2.25, 4.5].map((boundary) => boundary * activity.tokenCount / 1_000_000) as [number, number, number, number, number];
  const index = boundaries.findIndex((boundary) => value < boundary);
  return index < 0 ? "apex" : TASK_TIERS[index]!;
}

function creatorRoute(model: ModelView): CurrentPricingRouteView | null {
  const input = finite(model.msrpInputPricePerMillion);
  const output = finite(model.msrpOutputPricePerMillion);
  const cache = finite(model.msrpCacheReadPricePerMillion);
  const provenance = model.msrpProvenance;
  const set = model.currentPricingRoutes;
  // Whole-route Best may use only the exact creator row retained by the same
  // successful current endpoint observation. Scalar MSRP survives a failed or
  // stale refresh for the MSRP view, but it is not a current Best route.
  if (model.msrpAudited !== true || provenance?.routeStatus !== "official-route" || input === null || output === null
    || (model.msrpCacheReadPricePerMillion !== null && cache === null)
    || finite(provenance.appliedInputPricePerMillion) !== input
    || finite(provenance.appliedOutputPricePerMillion) !== output
    || (provenance.appliedCacheReadPricePerMillion === null
      ? cache !== null
      : finite(provenance.appliedCacheReadPricePerMillion) === null || finite(provenance.appliedCacheReadPricePerMillion) !== cache)
    || typeof provenance.providerName !== "string" || !provenance.providerName
    || typeof provenance.providerTag !== "string" || !provenance.providerTag
    || !standardCurrentRouteTag(provenance.providerName, provenance.providerTag)
    || !/^[0-9a-f]{64}$/.test(provenance.endpointSha256 ?? "")
    || !Number.isInteger(provenance.sourceRowIndex) || provenance.sourceRowIndex! < 0
    || !set || set.freshness === "stale"
    || set.requestedModelId !== model.id || set.responseModelId !== model.id || set.canonicalSlug !== model.canonicalSlug
    || set.observedAt !== provenance.observedAt || set.endpointUrl !== provenance.endpointUrl
    || set.endpointSha256 !== provenance.endpointSha256 || provenance.requestedModelId !== model.id
    || provenance.responseModelId !== model.id || provenance.canonicalSlug !== model.canonicalSlug) return null;
  const exactCurrentSet = set.status === "ok" && set.observedCanonicalSlug === model.canonicalSlug;
  // Migration bridge for observations retained before requested_canonical_slug
  // existed. Dashboard provenance has already rebound the exact hashed row and
  // index; no other failed status is accepted, and a later failed attempt has
  // a different observation tuple so cannot pass the checks above.
  const exactPreCanonicalSet = set.status === "failed" && set.observedCanonicalSlug === null && set.routes.length === 0
    && set.diagnostics.length === 1
    && set.diagnostics[0] === "Route set rejected: historical observation did not capture a canonical checkpoint.";
  if (!exactCurrentSet && !exactPreCanonicalSet) return null;
  const retainedRowAtIndex = set.routes.find((route) => route.kind === "openrouter" && route.sourceRowIndex === provenance.sourceRowIndex);
  // Not every official cache tariff is eligible as a generic OpenRouter route
  // (for example, conditional-cache metadata can be absent). When that row is
  // present in the generic subset it must agree; otherwise the independently
  // validated retained MSRP row remains the creator candidate.
  if (retainedRowAtIndex && (retainedRowAtIndex.provider !== provenance.providerName || retainedRowAtIndex.tag !== provenance.providerTag
    || retainedRowAtIndex.observationTime !== provenance.observedAt || retainedRowAtIndex.endpointUrl !== provenance.endpointUrl
    || retainedRowAtIndex.endpointSha256 !== provenance.endpointSha256)) return null;
  const sourceRowIndex = provenance.sourceRowIndex!;
  return {
    kind: "creator-msrp",
    cacheReadPricePerMillion: cache,
    inputPricePerMillion: input,
    outputPricePerMillion: output,
    provider: provenance.providerName,
    tag: provenance.providerTag,
    fingerprint: sha256Hex(JSON.stringify({ modelId: model.id, kind: "creator-msrp", provider: provenance.providerName,
      tag: provenance.providerTag, sourceRowIndex, rawPricing: provenance.rawPricing ?? null,
      endpointSha256: provenance.endpointSha256 })),
    observationTime: provenance.observedAt,
    endpointUrl: provenance.endpointUrl,
    endpointSha256: provenance.endpointSha256,
    sourceRowIndex,
  };
}

export function hasCurrentCreatorRoute(model: ModelView): boolean {
  return creatorRoute(model) !== null;
}

const NON_STANDARD_ROUTE_TOKENS = new Set(["batch", "express", "flex", "free", "highspeed", "priority", "realtime", "turbo", "zdr"]);
export function standardCurrentRouteTag(provider: string, tag: string): boolean {
  const identity = `${provider} ${tag}`.toLowerCase();
  const tokens = identity.split(/[^a-z0-9]+/).filter(Boolean);
  return !tokens.some((token) => NON_STANDARD_ROUTE_TOKENS.has(token))
    && !identity.replace(/[^a-z0-9]+/g, "").includes("highspeed");
}

function routeOrder(left: CurrentPricingRouteView, right: CurrentPricingRouteView): number {
  if (left.kind !== right.kind) return left.kind === "creator-msrp" ? -1 : 1;
  return left.provider.localeCompare(right.provider) || left.tag.localeCompare(right.tag) || left.fingerprint.localeCompare(right.fingerprint);
}

/** Select the cheapest complete current route as one atomic tuple. */
export function resolveWholeRouteBest(model: ModelView, activity: TaskFormula): WholeRoutePricingResult {
  const set = model.currentPricingRoutes;
  // Frozen v1-v4 payloads intentionally retain their historical flat Best.
  if (!set) {
    const input = finite(model.inputPricePerMillion), output = finite(model.outputPricePerMillion);
    const cacheRead = finite(model.cacheReadPricePerMillion);
    const value = input === null || output === null ? null : wholeRouteTaskCost({ cacheReadPricePerMillion: cacheRead, inputPricePerMillion: input, outputPricePerMillion: output }, activity);
    return { cacheRead, input, output, value, costTier: taskTier(value, activity), source: "OpenRouter best current price (legacy flat)", fallback: false, cacheFallback: value !== null && activity.cacheShare > 0 && cacheRead === null, isTaskPrice: true, routeKind: value === null ? "unavailable" : "legacy-flat", provider: null, tag: null, fingerprint: null, observationTime: null, endpointUrl: null, endpointSha256: null, freshness: "legacy", diagnostics: [] };
  }

  const creator = creatorRoute(model);
  const routesCurrent = set.status === "ok" && set.freshness !== "stale"
    && set.requestedModelId === model.id && set.responseModelId === model.id
    && set.observedCanonicalSlug === model.canonicalSlug && set.canonicalSlug === model.canonicalSlug;
  const candidates = [...set.routes, ...(creator ? [creator] : [])]
    .filter((route) => route.kind === "creator-msrp" || (routesCurrent
      && route.observationTime === set.observedAt && route.endpointUrl === set.endpointUrl && route.endpointSha256 === set.endpointSha256
      && standardCurrentRouteTag(route.provider, route.tag)))
    .filter((route) => finite(route.inputPricePerMillion) !== null && finite(route.outputPricePerMillion) !== null
      && (route.cacheReadPricePerMillion === null || finite(route.cacheReadPricePerMillion) !== null));
  const unique = new Map<string, CurrentPricingRouteView>();
  for (const route of candidates) {
    const signature = JSON.stringify([route.cacheReadPricePerMillion, route.inputPricePerMillion, route.outputPricePerMillion]);
    const incumbent = unique.get(signature);
    if (!incumbent || routeOrder(route, incumbent) < 0) unique.set(signature, route);
  }
  const priced = [...unique.values()].map((route) => ({ route, value: wholeRouteTaskCost(route, activity)! }))
    .filter(({ value }) => value !== null)
    .sort((left, right) => left.value - right.value || routeOrder(left.route, right.route));
  const winner = priced[0];
  if (!winner) return { cacheRead: null, input: null, output: null, value: null, costTier: "unknown", source: "Lowest current complete route unavailable", fallback: false, cacheFallback: false, isTaskPrice: true, routeKind: "unavailable", provider: null, tag: null, fingerprint: null, observationTime: set.observedAt, endpointUrl: set.endpointUrl, endpointSha256: set.endpointSha256, freshness: set.freshness, diagnostics: set.diagnostics };
  const route = winner.route;
  const source = route.kind === "creator-msrp"
    ? `${model.companyName} creator MSRP · Lowest current complete route`
    : `${route.provider}${route.tag ? ` / ${route.tag}` : ""} · Lowest current complete route`;
  return { cacheRead: route.cacheReadPricePerMillion, input: route.inputPricePerMillion, output: route.outputPricePerMillion, value: winner.value, costTier: taskTier(winner.value, activity), source, fallback: false, cacheFallback: activity.cacheShare > 0 && route.cacheReadPricePerMillion === null, isTaskPrice: true, routeKind: route.kind, provider: route.provider, tag: route.tag, fingerprint: route.fingerprint, observationTime: route.observationTime, endpointUrl: route.endpointUrl, endpointSha256: route.endpointSha256, freshness: set.freshness, diagnostics: set.diagnostics };
}
